import { logEvent } from "firebase/analytics";
import {
    signInWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider,
} from "firebase/auth";
import { auth, analytics } from "./firebase";
import { handleAuthError } from "../utils/errorHandler";

async function isUserLoggedIn(): Promise<Boolean> {
    await auth.authStateReady();
    return auth.currentUser !== null;
}

/**
 *
 * @param email User email
 * @param password User password
 * @returns A promise that resolves to true if the signIn was successful
 * @throws a sanitized error message (not the raw Firebase error)
 */
async function loginWithEmailAndPassword(email: string, password: string): Promise<Boolean> {
    try {
        await signInWithEmailAndPassword(auth, email, password);
        logEvent(analytics, "login", { method: "emailAndPassword" });
        return true;
    } catch (err) {
        // Use centralized error handler - logs to analytics and returns sanitized message
        const sanitizedError = handleAuthError(err, "loginWithEmailAndPassword");
        throw new Error(sanitizedError);
    }
}

/**
 * Sign in with Google using a popup window
 * @returns A promise that resolves to true if the signIn was successful
 * @throws a sanitized error message (not the raw Firebase error)
 */
async function loginWithGoogle(): Promise<Boolean> {
    try {
        const provider = new GoogleAuthProvider();
        await signInWithPopup(auth, provider);
        logEvent(analytics, "login", { method: "google" });
        return true;
    } catch (err) {
        // Use centralized error handler - logs to analytics and returns sanitized message
        const sanitizedError = handleAuthError(err, "loginWithGoogle");
        throw new Error(sanitizedError);
    }
}

async function logout() {
    try {
        await auth.signOut();
        logEvent(analytics, "logout");
    } catch (err) {
        // Log error but don't throw - logout should always succeed from user perspective
        handleAuthError(err, "logout");
    }
}

function getUserEmail() {
    if (auth.currentUser) {
        return auth.currentUser.email ? auth.currentUser.email : "";
    }

    return "";
}

export {
    isUserLoggedIn,
    loginWithEmailAndPassword,
    loginWithGoogle,
    logout,
    getUserEmail,
};
